export default {
  slug: "hyrox-training-cost-dallas",
  metaTitle: "HYROX Training Cost in Dallas: Classes, Packs & Sims",
  metaDescription:
    "What HYROX training costs in Dallas: $20 drop-in classes, $60 4-packs, $25 full simulations and a free Sunday session, plus what a 12-week build adds up to.",
  h1: "How Much Does HYROX Training Cost in Dallas? $20 Classes, $60 4-Packs, $25 Sims",
  category: "Classes & Booking",
  pillar: false,
  datePublished: "2026-10-06",
  dateModified: "2026-10-06",
  readTime: 7,
  hero: {
    src: "/dps/bg-home2.jpg",
    alt: "Training floor at Dallas Performance Systems during a HYROX session",
    caption: "Every price on this page buys time on this floor.",
  },
  summary:
    "HYROX training in Dallas costs $20 for a 90-minute drop-in specialty class, $60 for a 4-pack ($15 per class), and $25 for a full race simulation. The DPS Sunday open session is free to reserve. A typical 12-week build runs from $25 to around $410 depending on volume.",
  takeaways: [
    "Drop-in class: $20 · HYROX 4-Pack: $60 · Full simulation: $25 · DPS Sunday open session: free.",
    "No membership required: everything is pay-per-session with instant online booking.",
    "A once-a-week, 12-week build on 4-packs plus one sim totals $205.",
    "The free Sunday session makes the true minimum cost of starting HYROX training in Dallas $0.",
  ],
  body: [
    { t: "p", html: "No membership math, no contract asterisks. Every price in the Hyrox Ninja program is a flat session rate, so your 12-week cost is simple multiplication. Here's the full list and three realistic budgets." },

    { t: "h2", id: "price-list", text: "The Dallas price list" },
    {
      t: "table",
      head: ["Session", "Price", "What you get"],
      rows: [
        ["Specialty class drop-in", "$20", "90 coached minutes, standards enforced, follows the 12-week program"],
        ["HYROX 4-Pack", "$60", "4 classes at $15 each"],
        ["Full simulation", "$25", "The entire race, timed and judged in a seeded wave"],
        ["DPS Sunday open session", "Free", "60-minute community workout, capped at 24, reservation required"],
      ],
      note: "All sessions book online with instant confirmation; paid sessions check out through Stripe.",
    },
    { t: "p", html: 'Class times and the current calendar are on the <a href="/schedule">schedule page</a>; everything bookable right now is on the <a href="/events">events page</a>.' },

    { t: "h2", id: "twelve-week", text: "What a 12-week build actually costs" },
    {
      t: "table",
      head: ["Path", "Weekly volume", "12-week total"],
      rows: [
        ["Free path", "Sunday open sessions only", "$0 (+ $25 if you add the sim)"],
        ["Steady build", "1 class/week via 4-packs + 1 sim", "$205"],
        ["Committed build", "2 classes/week via 4-packs + 2 sims", "$410"],
      ],
      note: "4-pack math: 12 classes = three 4-packs ($180); 24 classes = six 4-packs ($360).",
    },
    { t: "p", html: 'For context on the sim line item, the $25 simulation is the single highest-value spend on this page: it\'s the full race, and the data it produces steers the other eleven weeks. Here\'s <a href="/blog/what-is-a-hyrox-simulation">why the simulation matters</a> if you\'re deciding whether to add it.' },

    { t: "h2", id: "vs-race", text: "How it compares to racing" },
    { t: "p", html: "An official HYROX race entry typically runs well north of $100 before travel. A full Dallas training cycle at the steady-build rate costs about two race entries, and it's the part that decides how the race goes." },

    { t: "h2", id: "policies", text: "Policies that affect your cost" },
    {
      t: "ul",
      items: [
        "<span>Booking opens 28 days out and closes 2 hours before a class.</span>",
        "<span>Cancel free up to 4 hours before; inside that window the session is charged.</span>",
        "<span>No-shows are charged the $20 session rate, so cancel instead of ghosting.</span>",
        "<span>Discount codes apply at checkout when we run them; the field is on the registration form.</span>",
      ],
    },

    { t: "h2", id: "home-gear", text: "The optional line item: home gear" },
    { t: "p", html: "Classes include all equipment, so gear spend is genuinely optional. It earns its money only if you'll train stations between sessions." },
    { t: "disclosure" },
    { t: "p", html: 'The sane starting points: the <a href="https://tidd.ly/49SlyJ4" rel="sponsored noopener" target="_blank">HYROX foundation bundle</a> for a one-purchase station setup, or a <a href="https://tidd.ly/4aYnqAZ" rel="sponsored noopener" target="_blank">home gym package</a> if you\'re building a garage setup anyway. Individual station pieces are itemized on the <a href="/gear">gear page</a>.' },

    { t: "h2", id: "cheapest-start", text: "The cheapest way to start this week" },
    { t: "p", html: 'Reserve the free <a href="/blog/hyrox-open-session-vs-specialty-class">Sunday open session</a>, spend $0, and see how your body answers. If the answer is \u201Cmore,\u201D the <a href="/blog/hyrox-classes-near-me-dallas">booking guide for Dallas classes</a> takes it from there.' },
  ],
  faqs: [
    { q: "Is there a membership or contract?", a: "No. Every session is pay-as-you-go. The 4-pack is the only bulk option and it never auto-renews." },
    { q: "What happens to my $25 if the simulation fills up?", a: "Your spot only confirms once payment clears, and if the event is full at that moment you're automatically refunded. No phantom charges, no manual chasing." },
    { q: "Do class prices include equipment?", a: "Yes. Sleds, bags, kettlebells, ergs and wall balls are all on the floor. You bring shoes and water." },
    { q: "Is the free Sunday session a trial with a sales pitch?", a: "No. It's a standing community workout, capped at 24. Plenty of regulars only ever do Sundays." },
  ],
  sources: [
    "Hyrox Ninja pricing and policies — hyroxninja.com/schedule",
    "Hyrox Ninja events and booking — hyroxninja.com/events",
  ],
  related: ["hyrox-classes-near-me-dallas", "what-is-a-hyrox-simulation", "hyrox-open-session-vs-specialty-class"],
  cta: {
    heading: "Start at whatever number works",
    html: "$0 gets you Sunday. $20 gets you coached. $205 gets you a full 12-week build with a measured finish.",
    to: "/schedule",
    label: "See Pricing & Schedule",
  },
};
